import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfigurationProvider } from '../configuration/configuration-provider';
import type { ConfigurationContextValue } from '../configuration/configuration-context';
import { CSRF_COOKIE, expireCookie } from '../http';
import { isGlobalAdmin as identityIsGlobalAdmin } from './access';
import type { PlatformSession } from './platform-session';
import { postLogin, postLogout } from './session-api';
import { SessionContext, type SessionContextValue } from './session-context';
import {
  accessQueryOptions,
  identityQueryOptions,
  orgTreeQueryOptions,
  resetScopeDependentQueries,
  sessionKeys,
} from './session-queries';

/**
 * The authenticated runtime for a portal app: who is logged in, which org node they are looking
 * at, what they may do there, and the feature configuration (`ConfigurationProvider`) for that
 * context. Replaces portal-ui-lib's AuthState / ScopeState / ConfigurationState wiring.
 *
 * Mount once, inside `QueryClientProvider`, around the router.
 */
export function PlatformProvider({
  platform,
  appId,
  children,
}: {
  platform: PlatformSession;
  /** Sent as `X-App-Id` on the layout request, e.g. `dealer-portal`. */
  appId: string;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const identityQuery = useQuery(identityQueryOptions(platform.client));
  const identity = identityQuery.data ?? null;
  const userId = identity?.userId ?? null;

  const treeQuery = useQuery({
    ...orgTreeQueryOptions(platform.client, userId ?? ''),
    enabled: userId !== null,
  });

  const scope = useSyncExternalStore(platform.scope.subscribe, platform.scope.getSnapshot);

  useEffect(() => {
    if (identity) {
      platform.scope.sync(identity, treeQuery.data ?? null);
    } else if (identityQuery.isSuccess) {
      platform.scope.clear();
    }
  }, [platform, identity, treeQuery.data, identityQuery.isSuccess]);

  // A different node or view mode: feature data fetched for the old one must not be shown.
  const scopeKey = scope.current ? `${scope.current.id}|${scope.viewMode}` : null;
  const previousScopeKey = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousScopeKey.current;
    previousScopeKey.current = scopeKey;
    if (previous !== null && scopeKey !== null && previous !== scopeKey) {
      void resetScopeDependentQueries(queryClient);
    }
  }, [scopeKey, queryClient]);

  // Asked for the selected node only once there is one, rather than once for the home node
  // and again a moment later.
  const accessQuery = useQuery({
    ...accessQueryOptions(platform.client, userId ?? '', scope.current?.id ?? null),
    enabled: userId !== null && scope.current !== null,
  });

  /**
   * Drop every cached answer except the identity query itself, which the provider is observing
   * — removing it would leave this component watching a query the cache no longer has.
   */
  const dropCachedData = useCallback(() => {
    queryClient.removeQueries({
      predicate: query => query.queryHash !== hashKey(sessionKeys.identity),
    });
  }, [queryClient]);

  /** Everything this tab knew about the user, gone. The server session is the caller's part. */
  const forgetSession = useCallback(() => {
    platform.scope.forgetAll();
    platform.activity.reset();
    expireCookie(CSRF_COOKIE);
    dropCachedData();
    queryClient.setQueryData(sessionKeys.identity, null);
  }, [platform, queryClient, dropCachedData]);

  // Only a session that existed can expire. A failed refresh while whoami is still finding out
  // whether there is one (or during login) is that answer, not an expiry — and clearing the
  // cache under that in-flight query would leave it pending.
  const identityRef = useRef(identity);
  useEffect(() => {
    identityRef.current = identity;
  }, [identity]);
  useEffect(
    () =>
      platform.onSessionExpired(() => {
        if (identityRef.current) {
          forgetSession();
        }
      }),
    [platform, forgetSession],
  );

  const login = useCallback<SessionContextValue['login']>(
    async credentials => {
      await postLogin(platform.client, credentials);
      // Nothing cached for whoever was here before may be read as the new user's.
      dropCachedData();
      return queryClient.fetchQuery({ ...identityQueryOptions(platform.client), staleTime: 0 });
    },
    [platform, queryClient, dropCachedData],
  );

  const logout = useCallback(async () => {
    try {
      await postLogout(platform.client);
    } catch {
      // Logged out locally regardless, as Angular did: the user asked to leave.
    }
    forgetSession();
  }, [platform, forgetSession]);

  const globalAdmin = identityIsGlobalAdmin(identity);

  const session = useMemo<SessionContextValue>(
    () => ({
      status: identityQuery.isPending
        ? 'pending'
        : identityQuery.isError
          ? 'error'
          : identity
            ? 'authenticated'
            : 'anonymous',
      identity,
      isGlobalAdmin: globalAdmin,
      scope,
      orgTree: treeQuery.data,
      access: accessQuery.data,
      login,
      logout,
      platform,
    }),
    [
      identityQuery.isPending,
      identityQuery.isError,
      identity,
      globalAdmin,
      scope,
      treeQuery.data,
      accessQuery.data,
      login,
      logout,
      platform,
    ],
  );

  const sessionJson = platform.client.json;
  const selectedOrgNodeId = scope.current?.id ?? null;
  const viewMode = scope.viewMode;
  const permissions = accessQuery.data?.permissions;
  const configuration = useMemo<ConfigurationContextValue>(
    () => ({
      client: sessionJson,
      appId,
      scope: userId ? { userId, orgNodeId: selectedOrgNodeId, viewMode } : null,
      isGlobalAdmin: globalAdmin,
      permissions,
    }),
    [sessionJson, appId, userId, selectedOrgNodeId, viewMode, globalAdmin, permissions],
  );

  return (
    <SessionContext value={session}>
      <ConfigurationProvider value={configuration}>{children}</ConfigurationProvider>
    </SessionContext>
  );
}
